import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useGetCities } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useUpdateCompanyMutation } from "src/api/add-sales";

interface IModel {
  open: boolean;
  companyId: number;
}

const schema = yup.object().shape({
  company_name: yup.string().required("Required"),
  branch_name: yup.string().required("Required"),
  city_id: yup.number().required("Branch location is required"),
  has_vat: yup.string().required("You have to select an option"),

  vat: yup.string().when("has_vat", {
    is: "yes",
    then: (value) => value.required("Required"),
  }),
});

interface FormInputs extends yup.InferType<typeof schema> {}

const FirstBranchPopup = (props: IModel) => {
  const { open, companyId } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");

  const { data: citiesData } = useGetCities();
  const { mutate, status, error } = useUpdateCompanyMutation(companyId);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { company_name, branch_name, city_id, has_vat, vat } = data;
    console.log({ data });

    const companyDataToSend = {
      company: {
        name: company_name,
      },
      branch: {
        name: branch_name,
        city_id: +city_id,
        has_vat: has_vat === "yes",
        vat: has_vat === "yes" ? (vat ? Number(vat) / 100 : 0) : 0,
      },
    };
    setLoading(true);
    mutate(companyDataToSend);
  };

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      const delayedExecution = setTimeout(() => {
        window.location.reload();
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: generalT("addedSuccessfully"),
          },
        ]);
      }, 4000);
      return () => clearTimeout(delayedExecution);
    } else if (status === "error") {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {t("addCompanyDetails")}
          </Typography>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="company_name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("companyName")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!errors.company_name}
                  helperText={errors.company_name?.message}
                />
              )}
            />
            <Controller
              name="branch_name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={generalT("branchName")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!errors.branch_name}
                  helperText={errors.branch_name?.message}
                />
              )}
            />
            <FormControl fullWidth size="small" error={!!errors.city_id}>
              <InputLabel id="demo-simple-select-label">
                {generalT("branchLocation")}
              </InputLabel>
              <Controller
                name="city_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("branchLocation")}
                  >
                    {citiesData?.data?.cities?.map(({ id, name }) => {
                      return (
                        <MenuItem key={id} value={`${id}`}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.city_id?.message}
              </FormHelperText>
            </FormControl>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={{ xs: 1, sm: 1, md: 3 }}
              flexWrap={"wrap"}
              width={"100%"}
            >
              <Typography color={"textSecondary"}>
                {t("areYourRegistered")}
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.has_vat}
                sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
              >
                <Controller
                  name="has_vat"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      key={field.value}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      <FormControlLabel
                        value="yes"
                        control={
                          <Radio
                            icon={<CircleOutlinedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                          />
                        }
                        label={generalT("yes")}
                        sx={{ color: "#98A2B3" }}
                      />
                      <FormControlLabel
                        value="no"
                        control={
                          <Radio
                            icon={<CircleOutlinedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                          />
                        }
                        label={generalT("no")}
                        sx={{ color: "#98A2B3" }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Stack>
            <FormHelperText error id="my-helper-text" sx={{ px: 2 }}>
              {errors.has_vat?.message}
            </FormHelperText>

            {watch("has_vat") === "yes" && (
              <Controller
                name="vat"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={`${generalT("vat")} %`}
                    {...field}
                    error={!!errors.vat}
                    helperText={errors.vat?.message}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Stack>
          <Stack mt={3} spacing={2} direction={"row"}>
            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              fullWidth
            >
              {generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default FirstBranchPopup;
